import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations } from './i18n';

const LangContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Deep-merge override tree onto a base tree. Returns a NEW object (never
 * mutates). Rules:
 *   • plain objects → merged key-by-key recursively
 *   • arrays        → element-wise merged when both are arrays (so one step's
 *                     text can be patched without blowing away the siblings)
 *   • otherwise     → override wins unless it is "", null or undefined, in
 *                     which case the base value is kept. Blank textarea → keep
 *                     shipped copy.
 */
function deepMerge(base, override) {
  if (override == null || override === '') return base;
  if (Array.isArray(base) && Array.isArray(override)) {
    const out = [];
    const len = Math.max(base.length, override.length);
    for (let i = 0; i < len; i += 1) {
      if (i < override.length && override[i] != null) {
        out.push(deepMerge(base[i], override[i]));
      } else {
        out.push(base[i]);
      }
    }
    return out;
  }
  if (
    base && typeof base === 'object' && !Array.isArray(base) &&
    override && typeof override === 'object' && !Array.isArray(override)
  ) {
    const out = { ...base };
    for (const key of Object.keys(override)) {
      out[key] = deepMerge(base[key], override[key]);
    }
    return out;
  }
  return override;
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem('sp-lang') || 'ka';
    } catch (e) {
      return 'ka';
    }
  });

  // CMS overrides fetched once at startup. Shape: {en:{...}, ka:{...}}.
  const [overrides, setOverrides] = useState({ en: {}, ka: {} });
  // Dynamic testimonials fetched from the admin manager (overrides the shipped
  // placeholder items on t.testimonials.items). Shape: [{en:{...}, ka:{...}}].
  const [testimonials, setTestimonials] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('sp-lang', lang); } catch (e) { /* ignore */ }
    document.documentElement.lang = lang;
  }, [lang]);

  // Fetch CMS content once. Any failure silently falls back to the shipped
  // dictionary so the public site never breaks because of a backend hiccup.
  useEffect(() => {
    let cancelled = false;
    const url = `${BACKEND_URL}/api/site-content`;
    fetch(url, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (data.overrides && typeof data.overrides === 'object') {
          setOverrides({
            en: data.overrides.en || {},
            ka: data.overrides.ka || {},
          });
        }
        if (data.testimonials && Array.isArray(data.testimonials.items)) {
          setTestimonials(data.testimonials.items);
        }
      })
      .catch(() => { /* keep i18n fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Build the active translation tree: shipped i18n → + CMS text overrides →
  // + CMS testimonial items. Memoisation isn't critical here, but we rebuild
  // only when inputs change.
  const base = translations[lang] || translations.en;
  const activeOverrides = overrides[lang] || {};
  let t = deepMerge(base, activeOverrides);

  if (testimonials && testimonials.length > 0) {
    // Flatten {en:{}, ka:{}} shape into locale-specific items while keeping
    // shipped eyebrow/title/subtitle intact (unless overridden above).
    const flatItems = testimonials.map((item) => item[lang] || item.en || {});
    t = {
      ...t,
      testimonials: {
        ...(t.testimonials || {}),
        items: flatItems,
      },
    };
  }

  /**
   * Pick the active locale's value from a {field, field_ka} pair.
   */
  const pick = (obj, basePath) => {
    if (!obj) return '';
    if (lang === 'ka') {
      const ka = obj[`${basePath}_ka`];
      if (ka != null && ka !== '') return ka;
    }
    return obj[basePath] ?? '';
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t, pick }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}
